/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { dateFieldDefault, invoiceNetValueNotOverpaid, percentageFieldDefault } from "../../../validationRules";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { ProductUnion } from "../../../../types/ProductUnion";
import { LitigationUnion } from "../../../../types/LitigationUnion";

export function extrajudicialInvoicingFields<T extends ProductUnion = ProductUnion>({ product }: { product: T }): FormElementPropsWithoutProduct<T>[] {
  return [
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isDisabled: ({ product }: { product: ProductUnion }) =>
        product.stage! >= stagesCase.STAGE_CHECKED && product.productClassName !== "dataLeakContract",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialFinal`,
      fieldType: "money",
      numberType: "float",
      isDisabled: ({ product }: { product: ProductUnion }) =>
        product.stage! >= stagesCase.STAGE_PREPARE_LAWSUIT && product.productClassName !== "dataLeakContract",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentExtrajudicial`,
      isDisabled: ({ product }: { product: ProductUnion }) =>
        !(
          product.stage < stagesCase.STAGE_PREPARE_LAWSUIT ||
          (product.stage === stagesCase.STAGE_LAWSUIT_COMPLETED && !product.costBearingByOpponentExtrajudicial)
        ),
      fieldType: "money",
      numberType: "float",
    },
  ];
}

export function judicialInvoicingFields({
  product,
}: {
  product: LitigationUnion;
}): FormElementPropsWithoutProduct<LitigationUnion>[] {
  return [
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isDisabled: ({ product }) =>
        product.stage! < stagesCase.STAGE_ACTION_REQUIRED && product.stage! > stagesCase.STAGE_PREPARE_LAWSUIT,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialFinal`,
      fieldType: "money",
      numberType: "float",
      isDisabled: ({ product }) => product.stage! != stagesCase.STAGE_TRIAL,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasExtrajudicialValueInDisputeOverwrite`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasExtrajudicialValueInDisputeOverwrite`),
      isDisabled: ({ product }) =>
        !product.valueInDisputeJudicialFinal ||
        product.valueInDisputeJudicialFinal ===
        product.valueInDisputeExtrajudicialFinal,
      dependentValidationFields: [
        `${product.productClassName}.valueInDisputeExtrajudicialFinal`,
        `${product.productClassName}.valueInDisputeJudicialFinal`,
      ]
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentFinalAbsolute`,
      validators: [...invoiceNetValueNotOverpaid(`${product.productClassName}.netRvgAmount`)],
      isDisabled: ({ product }) =>
        product.stage! !== stagesCase.STAGE_LAWSUIT_COMPLETED || !product.lawsuitCompletedDate,
      isHidden: ({ product }) => !product.caseWasInLitigation,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasStandardInvoicing`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasStandardInvoicing`),
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.subtractExtrajudicialProcessFee`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.subtractExtrajudicialProcessFee`),
      isDisabled: ({ product }) =>
        product.stage! !== stagesCase.STAGE_LAWSUIT &&
        product.stage! !== stagesCase.STAGE_TRIAL &&
        product.stage! !== stagesCase.STAGE_ACTION_REQUIRED &&
        product.stage! !== stagesCase.STAGE_PREPARE_LAWSUIT,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.netRvgAmount`,
      isDisabled: ({ product }) =>
        product.stage! !== stagesCase.STAGE_LAWSUIT_COMPLETED || !product.lawsuitCompletedDate,
      isHidden: ({ product }) => Boolean(product.hasStandardInvoicing),
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.courtCostsFinal`,
      isDisabled: ({ product }) =>
        product.stage! !== stagesCase.STAGE_LAWSUIT_COMPLETED || !product.lawsuitCompletedDate,

      isHidden: ({ product }) =>
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          product.lawsuitCompletedReason
        ) || product.costBearingByOpponentRelative === 0.0,
      numberType: "float",
      fieldType: "money",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentRelative`,
      validators: percentageFieldDefault,
      fieldType: "percentage",
      numberType: "float",
      isHidden: ({ product }) =>
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(product.lawsuitCompletedReason),
      isDisabled: ({ product }) =>
        ![stagesCase.STAGE_LAWSUIT_COMPLETED, stagesCase.STAGE_TRIAL, stagesCase.STAGE_LAWSUIT].includes(product.stage),
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.principalClaim`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          product.lawsuitCompletedReason
        ) ||
        product.costBearingByOpponentRelative === 0.0,
      isDisabled: ({ product }) => product.stage! !== stagesCase.STAGE_LAWSUIT,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.principalClaimDefaultDate`,
      isHidden: ({ values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          product.lawsuitCompletedReason
        ) ||
        product.costBearingByOpponentRelative === 0.0,
      isDisabled: ({ product }) => product.stage! !== stagesCase.STAGE_LAWSUIT,
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.accessoryClaim`,
      isHidden: ({ values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          product.lawsuitCompletedReason
        ) ||
        product.costBearingByOpponentRelative === 0.0,
      isDisabled: ({ product }) => product.stage! !== stagesCase.STAGE_LAWSUIT,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.accessoryClaimDefaultDate`,
      isHidden: ({ values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          product.lawsuitCompletedReason
        ) ||
        product.costBearingByOpponentRelative === 0.0,
      isDisabled: ({ product }) => product.stage! !== stagesCase.STAGE_LAWSUIT,
      validators: dateFieldDefault,
    },
  ];
}
